import React from 'react';
import EndpointCard from '../EndpointCard';
import CodeBlock from '../CodeBlock';

const CorpusMaps = () => {
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

  return (
    <section className="section">
      <h2 className="section-title">Corpus Maps</h2>
      <p className="text-gray-300 mb-6 leading-7 max-w-[65ch]">
        Generate high-level ontology and taxonomy visualizations from your knowledge graph. Corpus maps
        provide structured views of your dataset's conceptual organization, helping you understand the
        relationships and hierarchies in your knowledge base. Requires Neo4j to be enabled.
      </p>

      <EndpointCard
        method="POST"
        path="/namespaces/{namespace_uuid}/corpusMaps"
        title="Create Corpus Map Task"
        description="Creates a new corpus map generation task. Generates ontology and/or taxonomy from the knowledge graph asynchronously. Returns a task UUID to poll for completion."
        tags={['Authentication: Required', 'Async: Yes', 'Neo4j Required']}
        parameters={{
          path: {
            namespace_uuid: { required: true, description: 'The unique identifier of the namespace' }
          }
        }}
        requestBody={{
          generate_ontology: true,
          generate_taxonomy: true,
          options: {
            ontology_depth: 2,
            taxonomy_levels: 3,
            entity_types: null,
            min_relationships: 1
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Path Parameters:</h4>
            <table className="parameter-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>namespace_uuid</code></td>
                  <td>string (UUID)</td>
                  <td><span className="required-badge">Required</span></td>
                  <td>The unique identifier of the namespace</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Request Body:</h4>
            <CodeBlock code={`{
  "generate_ontology": true,
  "generate_taxonomy": true,
  "options": {
    "ontology_depth": 2,
    "taxonomy_levels": 3,
    "entity_types": null,
    "min_relationships": 1
  }
}`} />
            <p className="text-xs text-gray-400 mt-2">
              <strong>generate_ontology</strong> (optional, default: true): Generate ontology structure<br/>
              <strong>generate_taxonomy</strong> (optional, default: true): Generate taxonomy hierarchy<br/>
              <strong>options</strong> (optional): Configuration options for generation
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X POST "${BASE_URL}/namespaces/{namespace_uuid}/corpusMaps" \\
  -H "X-API-Key: email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "generate_ontology": true,
    "generate_taxonomy": true
  }'`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (202 Accepted):</h4>
            <CodeBlock code={`{
  "uuid": "{task_uuid}",
  "namespace_uuid": "{namespace_uuid}",
  "status": "PENDING",
  "generate_ontology": true,
  "generate_taxonomy": true,
  "options": null,
  "created_at": "2024-01-15T10:00:00Z",
  "updated_at": "2024-01-15T10:00:00Z"
}`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="GET"
        path="/namespaces/{namespace_uuid}/corpusMaps"
        title="List Corpus Map Tasks"
        description="Retrieves a paginated list of all corpus map tasks for a namespace. Can filter by status."
        tags={['Authentication: Required', 'Neo4j Required', 'Pagination: Supported']}
        parameters={{
          path: {
            namespace_uuid: { required: true, description: 'The unique identifier of the namespace' }
          },
          query: {
            status: { required: false, description: 'Filter by task status (PENDING, PROCESSING, COMPLETED, FAILED)' },
            skip: { required: false, description: 'Number of records to skip (default: 0)', default: 0 },
            limit: { required: false, description: 'Maximum number of records to return (default: 100, max: 100)', default: 100 }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Query Parameters:</h4>
            <table className="parameter-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Default</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>status</code></td>
                  <td>string</td>
                  <td><span className="optional-badge">Optional</span></td>
                  <td>-</td>
                  <td>Filter by task status</td>
                </tr>
                <tr>
                  <td><code>skip</code></td>
                  <td>integer</td>
                  <td><span className="optional-badge">Optional</span></td>
                  <td>0</td>
                  <td>Number of records to skip</td>
                </tr>
                <tr>
                  <td><code>limit</code></td>
                  <td>integer</td>
                  <td><span className="optional-badge">Optional</span></td>
                  <td>100</td>
                  <td>Maximum number of records (max: 100)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X GET "${BASE_URL}/namespaces/{namespace_uuid}/corpusMaps?status=COMPLETED&limit=50" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="GET"
        path="/namespaces/{namespace_uuid}/corpusMaps/{task_uuid}"
        title="Get Corpus Map Task"
        description="Retrieves the status and progress of a specific corpus map generation task."
        tags={['Authentication: Required', 'Neo4j Required']}
        parameters={{
          path: {
            namespace_uuid: { required: true, description: 'The unique identifier of the namespace' },
            task_uuid: { required: true, description: 'The unique identifier of the corpus map task' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X GET "${BASE_URL}/namespaces/{namespace_uuid}/corpusMaps/{task_uuid}" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`{
  "uuid": "{task_uuid}",
  "namespace_uuid": "{namespace_uuid}",
  "status": "COMPLETED",
  "generate_ontology": true,
  "generate_taxonomy": true,
  "options": null,
  "progress": {
    "stage": "COMPLETED",
    "message": "Corpus map generation completed successfully"
  },
  "result": null,
  "created_at": "2024-01-15T10:00:00Z",
  "updated_at": "2024-01-15T10:01:30Z"
}`} />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="GET"
        path="/namespaces/{namespace_uuid}/corpusMaps/{task_uuid}/results"
        title="Get Corpus Map Results"
        description="Retrieves the full corpus map results (ontology and/or taxonomy) for a completed task. Results can be filtered to include only ontology or only taxonomy."
        tags={['Authentication: Required', 'Neo4j Required']}
        parameters={{
          path: {
            namespace_uuid: { required: true, description: 'The unique identifier of the namespace' },
            task_uuid: { required: true, description: 'The unique identifier of the corpus map task' }
          },
          query: {
            include_ontology: { required: false, description: 'Include ontology in results (default: true)', default: true },
            include_taxonomy: { required: false, description: 'Include taxonomy in results (default: true)', default: true }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Query Parameters:</h4>
            <table className="parameter-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Default</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>include_ontology</code></td>
                  <td>boolean</td>
                  <td><span className="optional-badge">Optional</span></td>
                  <td>true</td>
                  <td>Include ontology in results</td>
                </tr>
                <tr>
                  <td><code>include_taxonomy</code></td>
                  <td>boolean</td>
                  <td><span className="optional-badge">Optional</span></td>
                  <td>true</td>
                  <td>Include taxonomy in results</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X GET "${BASE_URL}/namespaces/{namespace_uuid}/corpusMaps/{task_uuid}/results?include_ontology=true&include_taxonomy=false" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
            <p className="text-sm text-blue-200">
              <strong>Note:</strong> This endpoint requires the task to be in <code className="bg-blue-900/50 px-1 py-0.5 rounded">COMPLETED</code> status.
              Poll the task endpoint first to ensure completion.
            </p>
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="DELETE"
        path="/namespaces/{namespace_uuid}/corpusMaps/{task_uuid}"
        title="Delete Corpus Map Task"
        description="Permanently deletes a corpus map task and its results."
        tags={['Authentication: Required', 'Neo4j Required']}
        parameters={{
          path: {
            namespace_uuid: { required: true, description: 'The unique identifier of the namespace' },
            task_uuid: { required: true, description: 'The unique identifier of the corpus map task to delete' }
          }
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">cURL Example:</h4>
            <CodeBlock code={`curl -X DELETE "${BASE_URL}/namespaces/{namespace_uuid}/corpusMaps/{task_uuid}" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`} />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Response (200 OK):</h4>
            <CodeBlock code={`{
  "uuid": "{task_uuid}",
  "namespace_uuid": "{namespace_uuid}",
  "status": "COMPLETED",
  "generate_ontology": true,
  "generate_taxonomy": true
}`} />
          </div>
        </div>
      </EndpointCard>
    </section>
  );
};

export default CorpusMaps;
